import { MotiSigWeb } from '../client/MotiSigWeb';
import { cdnBaseFromScriptSrc } from '../push/serviceWorkerSync';
import type { MotiSigWebOptions } from '../types';

type QueuedCall = [method: string, ...args: unknown[]];

const METHODS = [
  'identify',
  'event',
  'updateUser',
  'getIdentity',
  'addTags',
  'removeTags',
  'addOrUpdateAttributes',
  'removeAttributes',
  'subscribePush',
  'unsubscribePush',
  'setPushEnabled',
  'getPushPermission',
  'logout',
  'reset',
] as const;

function findScriptEl(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement) return document.currentScript;
  const byAttr = document.querySelector('script[data-sdk-key][data-project-id]');
  if (byAttr instanceof HTMLScriptElement) return byAttr;
  const bySrc = Array.from(document.getElementsByTagName('script')).find((s) => /motisig/i.test(s.src));
  return bySrc ?? null;
}

function readOptionsFromScript(el: HTMLScriptElement | null): MotiSigWebOptions | null {
  if (!el) return null;
  const d = el.dataset;
  const sdkKey = d.sdkKey?.trim();
  const projectId = d.projectId?.trim();
  if (!sdkKey || !projectId) return null;
  const options: MotiSigWebOptions = { sdkKey, projectId };
  if (d.baseUrl) options.baseURL = d.baseUrl.trim();
  if (d.logLevel) options.logLevel = d.logLevel.trim() as MotiSigWebOptions['logLevel'];
  options.anonymous = {
    enabled: d.anonymous !== 'false',
    optOut: d.fingerprint !== 'true',
  };
  if (d.fingerprintUrl) options.anonymous.fingerprintCdnUrl = d.fingerprintUrl.trim();
  if (d.push === 'true') {
    options.push = {
      enabled: true,
      serviceWorkerPath: d.serviceWorkerPath?.trim() || '/motisig-sw.js',
      serviceWorkerScope: d.serviceWorkerScope?.trim() || '/',
      autoSubscribe: d.pushAutoSubscribe === 'true',
    };
    if (d.vapidPublicKey) options.push.vapidPublicKey = d.vapidPublicKey.trim();
    if (el.src) {
      const cdnBase = cdnBaseFromScriptSrc(el.src);
      if (cdnBase) options.push.cdnBaseUrl = cdnBase;
    }
  }
  return options;
}

(function bootstrap() {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { motisig?: unknown; MotiSigWeb?: typeof MotiSigWeb };

  const queued: QueuedCall[] = Array.isArray(w.motisig) ? (w.motisig as QueuedCall[]).slice() : [];
  const instance = new MotiSigWeb();

  const scriptEl = findScriptEl();

  let chain: Promise<unknown> = Promise.resolve();
  const enqueue = <T>(fn: () => Promise<T> | T): Promise<T> => {
    const next = chain.then(() => fn());
    chain = next.then(() => undefined, () => undefined);
    return next as Promise<T>;
  };

  const api = {
    version: __MOTISIG_VERSION__,
    instance,
    initialize: (options: MotiSigWebOptions) => enqueue(() => instance.initialize(options)),
    getAnonymousId: () => instance.getAnonymousId(),
    getAnonymousIdProvider: () => instance.getAnonymousIdProvider(),
    currentUserId: () => instance.currentUserId,
    getPushPermission: () => instance.getPushPermission(),
  } as Record<string, unknown>;

  for (const method of METHODS) {
    api[method] = (...args: unknown[]) =>
      enqueue(() => (instance[method] as (...a: unknown[]) => unknown)(...args));
  }

  w.motisig = api;
  w.MotiSigWeb = MotiSigWeb;

  for (const [method, ...args] of queued) {
    const fn = api[method];
    if (typeof fn === 'function') {
      try {
        (fn as (...a: unknown[]) => unknown)(...args);
      } catch {
        // ignore replay errors; logged inside SDK
      }
    }
  }

  const autoOptions = readOptionsFromScript(scriptEl);
  if (autoOptions) {
    enqueue(async () => {
      await instance.initialize(autoOptions);
      const autoPageView = scriptEl?.dataset.autoPageView !== 'false';
      if (autoPageView) {
        instance.enableAutoPageView();
      }
    });
  }
})();
